import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, TextField, Typography } from "@mui/material";
import { useDropzone } from "react-dropzone";
import AiChatAttachmentIcon from "../assets/aiChatAttachment.svg?react";
import { DotLegalTooltip, useDotLegalSnackbar } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "../../localization/useTranslation";
import { useAiChatStyles } from "../AiChat.styles";
import { AiChatAttachment, AiChatAttachmentStatus } from "../AiChat.types";
import { useAiChatAttachments, useAiChatAttachmentUpload, useDeleteAiChatAttachment } from "../attachments/AiChatAttachment.hooks";
import AiChatAttachmentChips from "../attachments/AiChatAttachmentChips";
import AiChatScopePicker from "../scope/AiChatScopePicker";
import AiChatScopeChips from "../scope/AiChatScopeChips";
import AiChatSendIcon from "../assets/aiChatSend.svg?react";
import AiChatStopIcon from "../assets/aiChatStop.svg?react";
import { useAiChatDocumentViewer } from "../documentViewer/AiChatDocumentViewerContext";
import { useAiSpendingWarning } from "../../aiAdministration/AiAdministration.hooks";
import { isAiSpendingExhausted } from "../../aiAdministration/AiAdministration.types";
import AiSpendingWarningAlert from "../../aiAdministration/AiSpendingWarningAlert";
import { useAiChatActions, useAiChatState } from "../AiChatContext";

interface AiChatInputProps {
    conversationId: string | null;
    ensureConversationId: () => Promise<string>;
    isStreaming: boolean;
    hasMessages: boolean;
    styles: ReturnType<typeof useAiChatStyles>;
    onSend: (text: string, readyAttachments: AiChatAttachment[]) => void;
    onStop: () => void;
}

function AiChatInput({ conversationId, ensureConversationId, isStreaming, hasMessages, styles, onSend, onStop }: AiChatInputProps) {
    const { translateString } = useTranslation();
    const { openAttachmentPreview } = useAiChatDocumentViewer();
    const { pendingPrompt } = useAiChatState();
    const { setPendingPrompt } = useAiChatActions();
    const [inputValue, setInputValue] = useState("");
    const inputRef = useRef<HTMLTextAreaElement | null>(null);

    // True both on mount (opening the panel lands on an empty chat) and when the user
    // starts a new chat from the header or sidebar, so one effect covers every entry point.
    const isShowingNewChat = conversationId === null && !hasMessages;

    useEffect(() => {
        if (isShowingNewChat) inputRef.current?.focus();
    }, [isShowingNewChat]);

    useEffect(() => {
        if (pendingPrompt === undefined) return;
        setInputValue(pendingPrompt);
        setPendingPrompt(undefined);
        // Defer focus until after the value has been committed, so the cursor lands at the
        // end of the staged text instead of position 0.
        requestAnimationFrame(() => {
            const el = inputRef.current;
            if (!el) return;
            el.focus();
            el.setSelectionRange(el.value.length, el.value.length);
        });
    }, [pendingPrompt, setPendingPrompt]);

    const { data: spendingWarning } = useAiSpendingWarning();
    const isAiExhausted = isAiSpendingExhausted(spendingWarning);

    const { data: attachments } = useAiChatAttachments(conversationId);
    const attachmentList = attachments ?? [];
    const hasProcessingAttachments = attachmentList.some((a) => a.status === AiChatAttachmentStatus.Processing);

    const { uploadFiles } = useAiChatAttachmentUpload({ ensureConversationId, currentAttachmentCount: attachmentList.length });
    const deleteAttachmentMutation = useDeleteAiChatAttachment();
    const snackbar = useDotLegalSnackbar();
    const [pendingDeleteIdsByConversation, setPendingDeleteIdsByConversation] = useState<Map<string, Set<string>>>(new Map());
    const pendingDeleteIds = conversationId ? (pendingDeleteIdsByConversation.get(conversationId) ?? new Set<string>()) : new Set<string>();
    const isDeletingAttachment = pendingDeleteIds.size > 0;

    function handleAttachmentDelete(attachmentId: string) {
        if (!conversationId || pendingDeleteIds.has(attachmentId)) return;
        const conversationIdAtClick = conversationId;
        setPendingDeleteIdsByConversation((prev) => {
            const next = new Map(prev);
            const existing = next.get(conversationIdAtClick) ?? new Set<string>();
            const updated = new Set(existing);
            updated.add(attachmentId);
            next.set(conversationIdAtClick, updated);
            return next;
        });
        deleteAttachmentMutation.mutate(attachmentId, {
            onSettled: (_data, error) => {
                setPendingDeleteIdsByConversation((prev) => {
                    const next = new Map(prev);
                    const existing = next.get(conversationIdAtClick);
                    if (!existing) return prev;
                    const updated = new Set(existing);
                    updated.delete(attachmentId);
                    if (updated.size === 0) {
                        next.delete(conversationIdAtClick);
                    } else {
                        next.set(conversationIdAtClick, updated);
                    }
                    return next;
                });
                if (error) {
                    snackbar.show(translateString("aiChatAttachmentDeleteFailed"), "error");
                }
            },
        });
    }

    const dropzone = useDropzone({
        onDrop: (files) => uploadFiles(files),
        multiple: true,
        noClick: true,
        noKeyboard: true,
    });

    function handleSend() {
        if (!inputValue.trim() || isStreaming || hasProcessingAttachments || isDeletingAttachment || isAiExhausted) return;
        const readyAttachments = attachmentList.filter((a) => a.status === AiChatAttachmentStatus.Ready);
        const text = inputValue;
        setInputValue("");
        onSend(text, readyAttachments);
    }

    function handleKeyDown(event: React.KeyboardEvent) {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            handleSend();
        }
    }

    return (
        <Box
            {...dropzone.getRootProps()}
            sx={{
                ...styles.inputArea,
                ...(dropzone.isDragActive && { outline: "2px dashed", outlineColor: "primary.main", outlineOffset: -4 }),
            }}
        >
            <input {...dropzone.getInputProps()} />
            <AiChatScopeChips conversationId={conversationId} />
            <AiChatAttachmentChips
                attachments={attachmentList}
                pendingDeleteIds={pendingDeleteIds}
                onPreview={openAttachmentPreview}
                onDelete={handleAttachmentDelete}
            />
            <Box sx={styles.inputWrapper}>
                <TextField
                    sx={styles.textField}
                    multiline
                    minRows={hasMessages ? 2 : 3}
                    maxRows={hasMessages ? 4 : 6}
                    placeholder={translateString("aiChatInputPlaceholder")}
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    variant="outlined"
                    size="small"
                    disabled={isAiExhausted}
                    inputRef={inputRef}
                />
                <Box sx={styles.inputControls}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <AiChatScopePicker conversationId={conversationId} />
                        <DotLegalTooltip text={translateString("aiChatAttachFile")}>
                            <IconButton size="small" onClick={dropzone.open} disabled={isAiExhausted} sx={{ "& svg": { width: 18, height: 18 }, ml: 1 }}>
                                <AiChatAttachmentIcon />
                            </IconButton>
                        </DotLegalTooltip>
                    </Box>
                    {isStreaming ? (
                        <DotLegalTooltip text={translateString("aiChatStopButtonTooltip")}>
                            <IconButton sx={styles.sendButton} onClick={onStop} size="small">
                                <AiChatStopIcon />
                            </IconButton>
                        </DotLegalTooltip>
                    ) : (
                        <IconButton
                            sx={styles.sendButton}
                            onClick={handleSend}
                            disabled={!inputValue.trim() || hasProcessingAttachments || isDeletingAttachment || isAiExhausted}
                            title={
                                isDeletingAttachment
                                    ? translateString("aiChatWaitForAttachmentDeleteTooltip")
                                    : hasProcessingAttachments
                                      ? translateString("aiChatWaitForAttachmentsTooltip")
                                      : undefined
                            }
                            size="small"
                        >
                            <AiChatSendIcon />
                        </IconButton>
                    )}
                </Box>
            </Box>
            <AiSpendingWarningAlert warning={spendingWarning} />
            <Typography sx={styles.disclaimer}>{translateString("aiChatDisclaimer")}</Typography>
        </Box>
    );
}

export default AiChatInput;
